import { Product } from '@components/ui/Product';
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select/Select';
import { Spinner } from '@shared/components/ui/Spinner';
import { TextArea } from '@shared/components/ui/textarea';
import { useToast } from '@shared/hooks/use-toast';
import { cn } from '@shared/utils/utils';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Check,
  Hash,
  ListChecks,
  MapPin,
  Package,
  PackageCheck,
  Undo2,
  Wrench,
} from 'lucide-react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AdjustPickingDiscrepancyPayload,
  AdjustPickingDiscrepancyResponse,
  InitialDiscrepancyContext,
  Preparation,
} from '../../models';
import { ORDER_STATUS_PICKING } from '../../models';
import { useAdjustDiscrepancy } from '../../hooks/useAdjustDiscrepancy';
import { useAdjustDiscrepancyPreview } from '../../hooks/useAdjustDiscrepancyPreview';
import { usePickingOrders } from '../../hooks/usePickingOrders';
import { computeSmartOrderSelection } from '../../utils/computeSmartOrderSelection';
import {
  getMaxDiscrepancyQty,
  getOtherLocationsWithStock,
  hasStockInOtherLocations,
} from '../../utils/discrepancyLocationStock';
import { orderHasProduct, sumSelectedOrdersProductQty } from '../../utils/orderHasProduct';
import { AdjustDiscrepancyOrderCard } from './AdjustDiscrepancyOrderCard';

type ModalStep = 'product' | 'orders' | 'preview';

const STEP_ORDER: ModalStep[] = ['product', 'orders', 'preview'];

export interface AdjustDiscrepancyModalRef {
  openModal: () => void;
  closeModal: () => void;
  openWithContext: (preparation: Preparation, initialDiscrepancy?: InitialDiscrepancyContext) => void;
}

interface AdjustDiscrepancyModalProps {
  onSuccess?: (response: AdjustPickingDiscrepancyResponse) => void;
}

const FIELD_LABEL_CLASS = 'flex items-center gap-1.5 text-sm font-medium text-foreground mb-2';

const StepIndicator = ({ currentStep }: { currentStep: ModalStep }) => {
  const { t } = useTranslation('pickingModule');
  const currentIndex = STEP_ORDER.indexOf(currentStep);

  return (
    <div className="flex items-center w-full mb-6" data-testid="adjust-discrepancy-step-indicator">
      {STEP_ORDER.map((step, index) => {
        const isCompleted = index < currentIndex;
        const isCurrent = index === currentIndex;
        return (
          <div key={step} className="flex items-center flex-1 last:flex-none min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <div
                className={cn(
                  'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-sm font-semibold transition-colors',
                  isCompleted && 'bg-primary border-primary text-primary-foreground',
                  isCurrent && 'border-primary bg-primary/10 text-primary',
                  !isCompleted && !isCurrent && 'border-border bg-muted/40 text-muted-foreground',
                )}
              >
                {isCompleted ? <Check className="h-4 w-4" /> : index + 1}
              </div>
              <span
                className={cn(
                  'hidden truncate text-sm transition-colors sm:inline',
                  isCurrent ? 'font-semibold text-foreground' : 'text-muted-foreground',
                )}
              >
                {t(`adjust_discrepancy.steps.${step}`)}
              </span>
            </div>
            {index < STEP_ORDER.length - 1 && (
              <div
                className={cn(
                  'mx-3 h-0.5 flex-1 rounded-full transition-colors',
                  index < currentIndex ? 'bg-primary' : 'bg-border',
                )}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

const AdjustDiscrepancyModal = forwardRef<AdjustDiscrepancyModalRef, AdjustDiscrepancyModalProps>(
  ({ onSuccess }, ref) => {
    const { t } = useTranslation('pickingModule');
    const { toast } = useToast();
    const internalModalRef = useRef<ModalCenterRef>(null);
    const pendingSmartSelectionRef = useRef(false);

    const [isOpen, setIsOpen] = useState(false);
    const [step, setStep] = useState<ModalStep>('product');
    const [preparation, setPreparation] = useState<Preparation | null>(null);
    const [selectedOrderIds, setSelectedOrderIds] = useState<Set<number>>(new Set());
    const [productId, setProductId] = useState<string>('');
    const [locationId, setLocationId] = useState<string>('');
    const [qty, setQty] = useState<string>('1');
    const [reason, setReason] = useState('');
    const [previewData, setPreviewData] = useState<AdjustPickingDiscrepancyResponse | null>(null);
    const [savedPayload, setSavedPayload] = useState<AdjustPickingDiscrepancyPayload | null>(null);

    const previewMutation = useAdjustDiscrepancyPreview();
    const applyMutation = useAdjustDiscrepancy();

    const preparationId = preparation?.id ?? 0;
    const parsedProductId = productId ? Number(productId) : 0;
    const hasProductSelected = parsedProductId > 0;

    const {
      data: orders = [],
      isLoading: ordersLoading,
      isError: ordersError,
    } = usePickingOrders(preparationId, isOpen && step === 'orders' && hasProductSelected);

    const pickingOrdersForProduct = useMemo(() => {
      if (!hasProductSelected) return [];
      return orders.filter((o) => o.status_id === ORDER_STATUS_PICKING && orderHasProduct(o, parsedProductId));
    }, [orders, hasProductSelected, parsedProductId]);

    const productLines = useMemo(
      () => (preparation?.lines ?? []).filter((line) => line.product_id),
      [preparation?.lines],
    );

    const selectedPreparationLine = useMemo(
      () => preparation?.lines.find((line) => line.product_id === parsedProductId),
      [preparation?.lines, parsedProductId],
    );

    const remainingToPick = useMemo(() => {
      if (!selectedPreparationLine) return 0;
      return Math.max(0, selectedPreparationLine.qty - selectedPreparationLine.qty_done);
    }, [selectedPreparationLine]);

    const resetForm = useCallback(() => {
      pendingSmartSelectionRef.current = false;
      setStep('product');
      setPreparation(null);
      setSelectedOrderIds(new Set());
      setProductId('');
      setLocationId('');
      setQty('1');
      setReason('');
      setPreviewData(null);
      setSavedPayload(null);
      previewMutation.reset();
      applyMutation.reset();
    }, [previewMutation, applyMutation]);

    const handleClose = useCallback(() => {
      internalModalRef.current?.closeModal();
      setIsOpen(false);
      resetForm();
    }, [resetForm]);

    const discrepancyLocations = useMemo(() => {
      if (!selectedPreparationLine) return [];
      const suggested = selectedPreparationLine.suggested_locations ?? [];
      const other = selectedPreparationLine.other_locations ?? [];
      const byId = new Map<number, { id: number; code: string; stock: number }>();
      [...suggested, ...other].forEach((loc) => {
        if (loc?.id) byId.set(loc.id, { id: loc.id, code: loc.code, stock: loc.stock ?? 0 });
      });
      return Array.from(byId.values());
    }, [selectedPreparationLine]);

    const parsedLocationIdForMax = Number(locationId) || 0;

    const maxDiscrepancyQty = useMemo(
      () => getMaxDiscrepancyQty(selectedPreparationLine, parsedLocationIdForMax, remainingToPick),
      [selectedPreparationLine, parsedLocationIdForMax, remainingToPick],
    );

    const defaultQtyForContext = useCallback((line: NonNullable<typeof selectedPreparationLine>, locId: number) => {
      const remaining = Math.max(0, line.qty - line.qty_done);
      const max = getMaxDiscrepancyQty(line, locId, remaining);
      if (max < 1) return '1';
      return String(Math.min(remaining > 0 ? remaining : max, max));
    }, []);

    const handleProductChange = (value: string) => {
      setProductId(value);
      setSelectedOrderIds(new Set());
      setLocationId('');
      const line = preparation?.lines.find((l) => l.product_id === Number(value));
      if (line) {
        const remaining = Math.max(1, line.qty - line.qty_done);
        setQty(String(remaining));
      }
    };

    const handleLocationChange = (value: string) => {
      setLocationId(value);
      if (!selectedPreparationLine) return;
      const locId = Number(value) || 0;
      const max = getMaxDiscrepancyQty(selectedPreparationLine, locId, remainingToPick);
      if (max < 1) return;
      const current = Number(qty) || 0;
      if (current > max || current < 1) {
        const remaining = Math.max(0, selectedPreparationLine.qty - selectedPreparationLine.qty_done);
        setQty(String(Math.min(remaining > 0 ? remaining : max, max)));
      }
    };

    const openWithContext = useCallback(
      (prep: Preparation, initialDiscrepancy?: InitialDiscrepancyContext) => {
        setPreparation(prep);
        setStep('product');
        setPreviewData(null);
        setSavedPayload(null);
        setSelectedOrderIds(new Set());
        const initialProductId = initialDiscrepancy?.product_id ? String(initialDiscrepancy.product_id) : '';
        setProductId(initialProductId);
        setLocationId(initialDiscrepancy?.location_id ? String(initialDiscrepancy.location_id) : '');
        if (initialDiscrepancy?.product_id) {
          const line = prep.lines.find((l) => l.product_id === initialDiscrepancy.product_id);
          const locId = initialDiscrepancy.location_id ?? 0;
          if (line) {
            if (initialDiscrepancy.qty) {
              const max = getMaxDiscrepancyQty(line, locId, Math.max(0, line.qty - line.qty_done));
              setQty(String(Math.min(initialDiscrepancy.qty, Math.max(1, max))));
            } else {
              setQty(defaultQtyForContext(line, locId));
            }
          } else {
            setQty(initialDiscrepancy.qty ? String(initialDiscrepancy.qty) : '1');
          }
        } else if (initialDiscrepancy?.qty) {
          setQty(String(initialDiscrepancy.qty));
        } else {
          setQty('1');
        }
        setReason('');
        setIsOpen(true);
        internalModalRef.current?.openModal();
      },
      [defaultQtyForContext],
    );

    useImperativeHandle(
      ref,
      () => ({
        openModal: () => {
          setIsOpen(true);
          internalModalRef.current?.openModal();
        },
        closeModal: handleClose,
        openWithContext,
      }),
      [handleClose, openWithContext],
    );

    const toggleOrder = (orderId: number, checked: boolean) => {
      setSelectedOrderIds((prev) => {
        const next = new Set(prev);
        if (checked) next.add(orderId);
        else next.delete(orderId);
        return next;
      });
    };

    const buildPayload = (): AdjustPickingDiscrepancyPayload | null => {
      if (!preparation) return null;
      const parsedLocationId = Number(locationId);
      const parsedQty = Number(qty);
      if (selectedOrderIds.size === 0 || !parsedProductId || !parsedLocationId || !parsedQty || parsedQty < 1) {
        return null;
      }
      const payload: AdjustPickingDiscrepancyPayload = {
        order_ids: Array.from(selectedOrderIds),
        discrepancy: {
          product_id: parsedProductId,
          location_id: parsedLocationId,
          qty: parsedQty,
        },
      };
      const trimmedReason = reason.trim();
      if (trimmedReason) {
        payload.reason = trimmedReason.slice(0, 255);
      }
      return payload;
    };

    const discrepancyQty = Number(qty) || 0;

    const selectedOrdersProductQty = useMemo(
      () => sumSelectedOrdersProductQty(pickingOrdersForProduct, selectedOrderIds, parsedProductId),
      [pickingOrdersForProduct, selectedOrderIds, parsedProductId],
    );

    const coversDiscrepancy =
      selectedOrderIds.size > 0 && selectedOrdersProductQty >= discrepancyQty && discrepancyQty >= 1;

    const parsedLocationId = Number(locationId) || 0;

    const otherLocationsWithStock = useMemo(() => {
      if (!selectedPreparationLine || !parsedLocationId) return [];
      return getOtherLocationsWithStock(selectedPreparationLine, parsedLocationId);
    }, [selectedPreparationLine, parsedLocationId]);

    const stockBlocksAdjust = hasStockInOtherLocations(selectedPreparationLine, parsedLocationId);

    const isStep1Valid =
      hasProductSelected &&
      parsedLocationId > 0 &&
      maxDiscrepancyQty >= 1 &&
      discrepancyQty >= 1 &&
      discrepancyQty <= maxDiscrepancyQty &&
      !stockBlocksAdjust;
    const selectedLocationCode = discrepancyLocations.find((loc) => String(loc.id) === locationId)?.code;
    const isStep2Valid = buildPayload() !== null && coversDiscrepancy;
    const isPending = previewMutation.isPending || applyMutation.isPending;

    const handleNextFromProduct = () => {
      if (stockBlocksAdjust) {
        toast({
          title: t('adjust_discrepancy.toast.stock_elsewhere_title'),
          description: t('adjust_discrepancy.toast.stock_elsewhere_description'),
          variant: 'destructive',
        });
        return;
      }
      if (!isStep1Valid) {
        toast({
          title: t('adjust_discrepancy.toast.validation_title'),
          description: t('adjust_discrepancy.toast.step1_validation'),
          variant: 'destructive',
        });
        return;
      }
      pendingSmartSelectionRef.current = true;
      setSelectedOrderIds(new Set());
      setStep('orders');
    };

    useEffect(() => {
      if (step !== 'orders' || ordersLoading || ordersError || !pendingSmartSelectionRef.current) {
        return;
      }
      if (pickingOrdersForProduct.length === 0) {
        pendingSmartSelectionRef.current = false;
        setSelectedOrderIds(new Set());
        return;
      }
      setSelectedOrderIds(computeSmartOrderSelection(pickingOrdersForProduct, parsedProductId, discrepancyQty));
      pendingSmartSelectionRef.current = false;
    }, [step, ordersLoading, ordersError, pickingOrdersForProduct, parsedProductId, discrepancyQty]);

    const handlePreview = async () => {
      if (!coversDiscrepancy) {
        toast({
          title: t('adjust_discrepancy.toast.validation_title'),
          description: t('adjust_discrepancy.toast.orders_coverage_validation', {
            required: discrepancyQty,
            selected: selectedOrdersProductQty,
          }),
          variant: 'destructive',
        });
        return;
      }
      const payload = buildPayload();
      if (!payload || !preparation) {
        toast({
          title: t('adjust_discrepancy.toast.validation_title'),
          description: t('adjust_discrepancy.toast.validation_description'),
          variant: 'destructive',
        });
        return;
      }
      try {
        const result = await previewMutation.mutateAsync({
          preparationId: preparation.id,
          payload,
        });
        setSavedPayload(payload);
        setPreviewData(result);
        setStep('preview');
      } catch {
        // onError toast handled in hook
      }
    };

    const handleApply = async () => {
      if (!preparation || !savedPayload) return;
      try {
        const result = await applyMutation.mutateAsync({
          preparationId: preparation.id,
          payload: savedPayload,
        });
        toast({
          title: t('adjust_discrepancy.toast.success_title'),
          description: t('adjust_discrepancy.toast.success_description'),
        });
        onSuccess?.(result);
        handleClose();
      } catch {
        // onError toast handled in hook
      }
    };

    const previewLines = previewData?.preparation?.lines?.filter(
      (line) => line.product_id === previewData.discrepancy.product_id,
    );

    const renderFooter = () => {
      if (step === 'product') {
        return (
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleClose} disabled={isPending}>
              {t('buttons.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleNextFromProduct}
              disabled={!isStep1Valid}
              data-testid="adjust-discrepancy-next-orders"
            >
              {t('adjust_discrepancy.next_button')}
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        );
      }
      if (step === 'orders') {
        return (
          <div className="flex items-center justify-between gap-2">
            <Button variant="outline" onClick={() => setStep('product')} disabled={isPending}>
              <ArrowLeft className="mr-1.5 h-4 w-4" />
              {t('adjust_discrepancy.back_button')}
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleClose} disabled={isPending}>
                {t('buttons.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={handlePreview}
                disabled={!isStep2Valid || isPending}
                data-testid="adjust-discrepancy-preview-button"
              >
                {previewMutation.isPending && <Spinner size="sm" className="mr-2" />}
                {t('adjust_discrepancy.preview_button')}
              </Button>
            </div>
          </div>
        );
      }
      return (
        <div className="flex items-center justify-between gap-2">
          <Button variant="outline" onClick={() => setStep('orders')} disabled={isPending}>
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            {t('adjust_discrepancy.back_button')}
          </Button>
          <Button
            variant="primary"
            onClick={handleApply}
            disabled={isPending}
            data-testid="adjust-discrepancy-confirm-button"
          >
            {applyMutation.isPending && <Spinner size="sm" className="mr-2" />}
            {t('adjust_discrepancy.confirm_button')}
          </Button>
        </div>
      );
    };

    return (
      <ModalCenter
        ref={internalModalRef}
        title={t('adjust_discrepancy.modal_title')}
        icon={<Wrench className="h-4 w-4 text-primary" />}
        type="default"
        width="xxl"
        maxHeight="90vh"
        classNameBody="overflow-y-auto"
        onCloseModal={resetForm}
        footer={renderFooter()}
      >
        <StepIndicator currentStep={step} />

        {step === 'product' && (
          <div className="space-y-5" data-testid="adjust-discrepancy-step-product">
            <p className="text-sm text-muted-foreground">{t('adjust_discrepancy.step_product_description')}</p>

            <div>
              <label className={FIELD_LABEL_CLASS}>
                <Package className="h-4 w-4 text-muted-foreground" />
                {t('adjust_discrepancy.product_label')}
              </label>
              <Select value={productId} onValueChange={handleProductChange}>
                <SelectTrigger className="w-full" data-testid="adjust-discrepancy-product-select">
                  <SelectValue placeholder={t('adjust_discrepancy.product_placeholder')} />
                </SelectTrigger>
                <SelectContent>
                  {productLines.map((line) => (
                    <SelectItem key={line.id} value={String(line.product_id)}>
                      {line.product?.name ?? line.product?.code ?? `Producto ${line.product_id}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedPreparationLine && (
              <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3 transition-colors">
                {selectedPreparationLine.product && (
                  <Product
                    id={selectedPreparationLine.product_id}
                    src={selectedPreparationLine.product.image || ''}
                    name={selectedPreparationLine.product.name}
                    code={selectedPreparationLine.product.code}
                    showImageModal={false}
                  />
                )}
                <div className="grid grid-cols-3 divide-x divide-border rounded-md border border-border bg-background text-center">
                  <div className="px-2 py-2.5">
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                      {t('adjust_discrepancy.prep_line.total')}
                    </p>
                    <p className="text-lg font-semibold text-foreground">{selectedPreparationLine.qty}</p>
                  </div>
                  <div className="px-2 py-2.5">
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                      {t('adjust_discrepancy.prep_line.picked')}
                    </p>
                    <p className="text-lg font-semibold text-foreground">{selectedPreparationLine.qty_done}</p>
                  </div>
                  <div className="px-2 py-2.5">
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                      {t('adjust_discrepancy.prep_line.remaining')}
                    </p>
                    <p className="text-lg font-semibold text-orange-600 dark:text-orange-400">{remainingToPick}</p>
                  </div>
                </div>
              </div>
            )}

            {hasProductSelected && (
              <div>
                <label className={FIELD_LABEL_CLASS}>
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  {t('adjust_discrepancy.location_label')}
                </label>
                {discrepancyLocations.length > 0 ? (
                  <Select value={locationId} onValueChange={handleLocationChange}>
                    <SelectTrigger className="w-full" data-testid="adjust-discrepancy-location-select">
                      <SelectValue placeholder={t('adjust_discrepancy.location_placeholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {discrepancyLocations.map((loc) => (
                        <SelectItem key={loc.id} value={String(loc.id)}>
                          {t('adjust_discrepancy.location_option', { code: loc.code, stock: loc.stock })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    type="number"
                    min={1}
                    value={locationId}
                    onChange={(e) => setLocationId(e.target.value)}
                    data-testid="adjust-discrepancy-location-input"
                    placeholder={t('adjust_discrepancy.location_id_placeholder')}
                  />
                )}
                <p className="text-xs text-muted-foreground mt-1.5">{t('adjust_discrepancy.location_hint')}</p>
              </div>
            )}

            {stockBlocksAdjust && otherLocationsWithStock.length > 0 && (
              <div
                className="flex gap-3 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
                role="alert"
                data-testid="adjust-discrepancy-stock-elsewhere-alert"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  <p className="font-medium">{t('adjust_discrepancy.stock_elsewhere_title')}</p>
                  <p className="mt-1 text-xs text-destructive/90">
                    {t('adjust_discrepancy.stock_elsewhere_description')}
                  </p>
                  <ul className="mt-2 flex flex-wrap gap-1.5">
                    {otherLocationsWithStock.map((loc) => (
                      <li
                        key={loc.id}
                        className="rounded-md border border-destructive/30 bg-destructive/10 px-2 py-0.5 text-xs font-medium"
                      >
                        {t('adjust_discrepancy.stock_elsewhere_location', {
                          code: loc.code,
                          stock: loc.stock,
                        })}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            <div>
              <label className={FIELD_LABEL_CLASS}>
                <Hash className="h-4 w-4 text-muted-foreground" />
                {t('adjust_discrepancy.missing_qty_label')}
              </label>
              <Input
                type="text"
                inputMode="numeric"
                autoComplete="off"
                selectAllOnFocus={false}
                value={qty}
                onChange={(e) => {
                  const next = e.target.value;
                  if (next === '' || /^\d+$/.test(next)) {
                    if (next !== '' && maxDiscrepancyQty >= 1 && Number(next) > maxDiscrepancyQty) {
                      setQty(String(maxDiscrepancyQty));
                      return;
                    }
                    setQty(next);
                  }
                }}
                data-testid="adjust-discrepancy-qty-input"
              />
              <p className="text-xs text-muted-foreground mt-1.5">
                {parsedLocationId > 0
                  ? t('adjust_discrepancy.missing_qty_hint_with_max', { max: maxDiscrepancyQty })
                  : t('adjust_discrepancy.missing_qty_hint')}
              </p>
            </div>
          </div>
        )}

        {step === 'orders' && (
          <div className="space-y-5" data-testid="adjust-discrepancy-step-orders">
            <p className="text-sm text-muted-foreground">{t('adjust_discrepancy.step_orders_description')}</p>

            {selectedPreparationLine && (
              <div
                className="rounded-md border border-border bg-muted/30 px-3 py-2 flex flex-wrap items-center justify-between gap-3"
                data-testid="adjust-discrepancy-selected-product-summary"
              >
                {selectedPreparationLine.product ? (
                  <Product
                    id={selectedPreparationLine.product_id}
                    src={selectedPreparationLine.product.image || ''}
                    name={selectedPreparationLine.product.name}
                    code={selectedPreparationLine.product.code}
                    showImageModal={false}
                  />
                ) : (
                  <span className="text-sm font-medium text-foreground">
                    {t('adjust_discrepancy.product_fallback', { id: selectedPreparationLine.product_id })}
                  </span>
                )}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs shrink-0">
                  {(selectedLocationCode || locationId) && (
                    <span className="text-muted-foreground">
                      {t('adjust_discrepancy.location_summary')}:{' '}
                      <span className="font-semibold text-foreground">{selectedLocationCode ?? locationId}</span>
                    </span>
                  )}
                  <span className="text-muted-foreground">
                    {t('adjust_discrepancy.prep_line.remaining')}:{' '}
                    <span className="font-semibold text-orange-600 dark:text-orange-400">{remainingToPick}</span>
                  </span>
                  <span className="text-muted-foreground">
                    {t('adjust_discrepancy.missing_qty_short')}:{' '}
                    <span className="font-semibold text-foreground">{qty}</span>
                  </span>
                </div>
              </div>
            )}

            {ordersLoading && (
              <div className="flex items-center justify-center gap-2 py-12 text-muted-foreground">
                <Spinner size="sm" />
                {t('adjust_discrepancy.orders_loading')}
              </div>
            )}
            {ordersError && <p className="text-sm text-destructive">{t('adjust_discrepancy.orders_error')}</p>}
            {!ordersLoading && !ordersError && pickingOrdersForProduct.length === 0 && (
              <p className="text-sm text-muted-foreground py-8 text-center">
                {t('adjust_discrepancy.no_picking_orders_for_product')}
              </p>
            )}
            {!ordersLoading && pickingOrdersForProduct.length > 0 && (
              <>
                <div
                  className={cn(
                    'rounded-lg border px-4 py-3 text-sm transition-colors',
                    coversDiscrepancy
                      ? 'border-green-500/40 bg-green-500/10 text-green-700 dark:text-green-300'
                      : 'border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-200',
                  )}
                  data-testid="adjust-discrepancy-orders-coverage"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1.5 font-medium">
                      {coversDiscrepancy && <Check className="h-4 w-4" />}
                      {t('adjust_discrepancy.coverage_short')}
                    </span>
                    <span className="font-semibold tabular-nums">
                      {selectedOrdersProductQty} / {discrepancyQty} {t('adjust_discrepancy.units_short')}
                    </span>
                  </div>
                  <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-foreground/10">
                    <div
                      className={cn(
                        'h-full rounded-full transition-all',
                        coversDiscrepancy ? 'bg-green-500' : 'bg-amber-500',
                      )}
                      style={{
                        width: `${discrepancyQty > 0 ? Math.min(100, (selectedOrdersProductQty / discrepancyQty) * 100) : 0}%`,
                      }}
                    />
                  </div>
                  {!coversDiscrepancy && (
                    <p className="mt-2 text-xs opacity-90">
                      {t('adjust_discrepancy.orders_coverage_insufficient', {
                        required: discrepancyQty,
                        missing: Math.max(0, discrepancyQty - selectedOrdersProductQty),
                      })}
                    </p>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">{t('adjust_discrepancy.orders_preselect_hint')}</p>
                <div className="flex flex-col gap-3 w-full max-h-[min(50vh,420px)] overflow-y-auto pr-1">
                  {pickingOrdersForProduct.map((order) => (
                    <AdjustDiscrepancyOrderCard
                      key={order.id}
                      order={order}
                      productId={parsedProductId}
                      selected={selectedOrderIds.has(order.id)}
                      onToggle={(checked) => toggleOrder(order.id, checked)}
                    />
                  ))}
                </div>
              </>
            )}

            <div className="pt-1">
              <label className={FIELD_LABEL_CLASS}>{t('adjust_discrepancy.reason_label')}</label>
              <TextArea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={255}
                placeholder={t('adjust_discrepancy.reason_placeholder')}
                rows={2}
              />
            </div>
          </div>
        )}

        {step === 'preview' && previewData && (
          <div className="space-y-4" data-testid="adjust-discrepancy-preview-step">
            <div className="flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-xs font-medium text-primary">
              <Badge variant="secondary" className="shrink-0 text-[11px]">
                {t('adjust_discrepancy.preview_badge_short')}
              </Badge>
              <span className="text-muted-foreground">{t('adjust_discrepancy.preview_badge_hint')}</span>
            </div>

            {!previewData.discrepancy.matching_pick_records_found_for_product_at_location && (
              <div
                className="flex gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-300"
                role="alert"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{t('adjust_discrepancy.no_matching_picks_warning')}</span>
              </div>
            )}

            <div className="rounded-lg border border-border p-3.5 space-y-2.5">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <Undo2 className="h-4 w-4 text-muted-foreground" />
                {t('adjust_discrepancy.rollbacks_title')}
              </h3>
              {previewData.order_rollbacks.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('adjust_discrepancy.rollbacks_empty')}</p>
              ) : (
                <ul className="space-y-1.5 text-sm">
                  {previewData.order_rollbacks.map((rb) => (
                    <li
                      key={rb.order_id}
                      className="flex items-center justify-between gap-2 rounded-md bg-muted/40 px-2.5 py-1.5"
                      data-testid={`adjust-discrepancy-rollback-${rb.order_id}`}
                    >
                      <span className="font-medium text-foreground">#{rb.order_id}</span>
                      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        {rb.from_state}
                        <ArrowRight className="h-3 w-3" />
                        {rb.to_state}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="rounded-lg border border-border p-3.5 space-y-2.5">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <PackageCheck className="h-4 w-4 text-muted-foreground" />
                {t('adjust_discrepancy.stock_returns_title')}
              </h3>
              {previewData.stock_returned_from_cart_to_locations.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('adjust_discrepancy.stock_returns_empty')}</p>
              ) : (
                <ul className="space-y-1 text-sm">
                  {previewData.stock_returned_from_cart_to_locations.map((item) => (
                    <li
                      key={item.pick_id}
                      className="text-muted-foreground"
                      data-testid={`adjust-discrepancy-stock-return-${item.pick_id}`}
                    >
                      {t('adjust_discrepancy.stock_return_item', {
                        productId: item.product_id,
                        qty: item.qty_returned,
                        from: item.from_bucket_location_code ?? item.from_bucket_location_id,
                        to: item.to_source_location_code ?? item.to_source_location_id,
                      })}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {previewLines && previewLines.length > 0 && (
              <div className="rounded-lg border border-border p-3.5 space-y-2.5">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
                  <ListChecks className="h-4 w-4 text-muted-foreground" />
                  {t('adjust_discrepancy.lines_title')}
                </h3>
                <ul className="space-y-1.5 text-sm">
                  {previewLines.map((line) => (
                    <li
                      key={line.id}
                      className="flex items-center justify-between gap-2 rounded-md bg-muted/40 px-2.5 py-1.5"
                    >
                      <span className="text-foreground">{line.product?.name ?? `Producto ${line.product_id}`}</span>
                      <span className="font-medium tabular-nums text-muted-foreground">
                        {line.qty_done} / {line.qty}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </ModalCenter>
    );
  },
);

AdjustDiscrepancyModal.displayName = 'AdjustDiscrepancyModal';

export default AdjustDiscrepancyModal;
